import React, { useImperativeHandle } from 'react';
import { Box, Chip } from '@mui/material';
import CommonInput from './common-input.component';
import { SelectOption, selectOptionLabelToPlainString } from './base-input.component';
import i18n from '../../i18n';
import { Option } from './select-input-multiple'
import SearchComponent from './search-input.component';
import _ from 'lodash';
import { Close } from '@mui/icons-material';
import moment from 'moment';
import GlobalNotification from '../common/snackbar';
import PrefixLabeledPillSelect from '../prefix-labeled-pill-select.component';
import { PILL_FILTER_CONTROL_WIDTH_SX } from '../pill-filter-control-sx';


/**
 * 搜索关键词输入框
 */
// eslint-disable-next-line react/display-name
export const SearchKeyInput = React.forwardRef((props: {
    items: SearchKeyInputItems[];
    // 当前全部筛选条件
    conditions: ConditionType[];
    // 右边的onChange 返回条件数组
    onSearch: (conditions: ConditionType[]) => void;
    // 显示已选条件
    showConditionsBox?: boolean;
    style?: React.CSSProperties;
    className?: string;
    onSearchKeyChange?: (key: string) => void;
    /**
     * 左侧为 TEXT 时，在用户回车、点击搜索图标、或 `SearchComponent` 触发 `onSearch`（含清空）时回调，
     * 与 `createCondition` 的提交时机一致，用于按提交关键词做本地过滤。
     */
    onTextValueChange?: (key: string, value: string) => void;
    /** 为 true 时下拉使用 PrefixLabeledPillSelect，与 PeriodDateFilter 药丸样式一致 */
    pillSelectStyle?: boolean;
}, ref: React.ForwardedRef<unknown>) => {
    const { items, showConditionsBox, onTextValueChange, pillSelectStyle } = props;
    const [searchKey, setSearchKey] = React.useState('');
    const [selectKey, setSelectKey] = React.useState('');
    const [dateTimeData, setDateTimeData] = React.useState<{
        startDate: Date | null,
        endDate: Date | null,
    }>({
        startDate: new Date(new Date().setHours(4, 0, 0)),
        endDate: new Date(new Date(new Date().setDate(new Date().getDate() + 1)).setHours(4, 0, 0))
    });

    useImperativeHandle(ref, () => ({
        searchKey,
        setSelectKey: (val: string) => setSelectKey(val),
    }));

    React.useEffect(() => {
        setSearchKey(String(items[0]?.value || ''));
    }, [])

    /**
     * 创建条件
     * @param condition
     */
    const createCondition = (condition: ConditionType) => {
        const { conditions } = props;
        // 找出key相同且值相同的条件
        const sameConditions = _.filter(conditions, item => {
            return item && condition && (item.key === condition.key) && (_.isEqual(item.value, condition.value));
        })
        if (sameConditions.length === 0) {
            conditions.push(condition);
        }
        props.onSearch(conditions)
    }


    const searchItem = items.find((item) => item.value === searchKey);
    const searchRef = React.useRef<{ setSearchValue: (val: string) => void }>();

    /** 筛选字段下拉：药丸样式与 PeriodDateFilter 一致 */
    const fieldKeySelect = pillSelectStyle ? (
        <PrefixLabeledPillSelect
            options={items.map((item) => ({
                value: String(item.value ?? ''),
                label: selectOptionLabelToPlainString(item.label),
            }))}
            value={searchKey}
            onChange={(value) => {
                setSearchKey(value);
                props.onSearchKeyChange?.(value);
                setSelectKey('');
                searchRef?.current?.setSearchValue?.('');
            }}
            sx={PILL_FILTER_CONTROL_WIDTH_SX}
        />
    ) : (
        <CommonInput
            inputType={'SELECT'}
            select={true}
            items={items}
            size={'small'}
            fullWidth
            value={searchKey}
            onChange={(e) => {
                setSearchKey(e.target.value);
                props.onSearchKeyChange?.(e.target.value);
                setSelectKey('');
                searchRef?.current?.setSearchValue?.('');
            }}
        />
    );

    /**
     * 筛选值下拉（SELECT 类型字段）：药丸样式
     */
    const fieldValueSelect =
        searchItem?.type === 'SELECT' ? (
            pillSelectStyle ? (
                <PrefixLabeledPillSelect
                    options={[
                        { value: '-1', label: i18n.t('google_text_please_select') },
                        ...(searchItem.options || []).map((opt) => ({
                            value: String(opt.value ?? ''),
                            label: selectOptionLabelToPlainString(opt.label),
                        })),
                    ]}
                    value={selectKey === '' ? '-1' : selectKey}
                    onChange={(value) => {
                        if (value === '-1') {
                            return;
                        }
                        const obj = searchItem.options?.find((item) => item.value === value);
                        setSelectKey(value);
                        createCondition({
                            value,
                            key: searchKey,
                            selectValue: selectOptionLabelToPlainString(obj?.label),
                            label: selectOptionLabelToPlainString(searchItem?.label),
                        });
                    }}
                    sx={PILL_FILTER_CONTROL_WIDTH_SX}
                />
            ) : (
                <CommonInput
                    style={{ minWidth: '200px' }}
                    inputType={'SELECT'}
                    items={[
                        { value: '-1', label: i18n.t('google_text_please_select'), disabled: true },
                        ...(searchItem.options || []),
                    ]}
                    value={selectKey === '' ? '-1' : selectKey}
                    onChange={(e) => {
                        const value = e.target.value;
                        const obj = searchItem.options?.find((item) => item.value === value);
                        if (value) {
                            setSelectKey(value);
                            createCondition({
                                value,
                                key: searchKey,
                                selectValue: selectOptionLabelToPlainString(obj?.label),
                                label: selectOptionLabelToPlainString(searchItem?.label),
                            });
                        }
                    }}
                />
            )
        ) : null;

    return <Box style={props.style} className={props.className}>
        <Box display={'flex'} sx={pillSelectStyle ? { alignItems: 'center', gap: 2, flexWrap: 'wrap' } : undefined}>
            {/* 筛选字段下拉 */}
            <Box style={pillSelectStyle ? undefined : { marginRight: '24px' }}>
                {fieldKeySelect}
            </Box>
            {/* 搜索框 / 筛选值下拉 */}
            <Box>
                {fieldValueSelect}
                {searchItem?.type === 'TEXT' &&
                    <SearchComponent
                        ref={searchRef}
                        pillStyle={pillSelectStyle}
                        {...{
                            allowClear: true,
                            placeholder: searchItem.textPlaceholder,
                            onSearch: (value) => {
                                // 与回车/点搜索/清空 同步，供外层本地过滤，不在每次输入 onChange 时触发
                                onTextValueChange?.(searchKey, value);
                                if (value.trim()) {
                                    createCondition({
                                        value,
                                        key: searchKey,
                                        selectValue: value,
                                        label: selectOptionLabelToPlainString(searchItem?.label),
                                    })
                                }
                            },
                        }}
                    />
                }
                {searchItem?.type === 'DATE_TIME_RANGE' &&
                    <Box sx={{ width: 370 }}>
                        <CommonInput
                            inputType={'DATE_TIME_RANGE'}
                            label={i18n.t('operation_record_operation_time')}
                            rangeHandleChangePicker={(dates) => {
                                if (dates) {
                                    const startDate = dates[0] ? moment(dates[0]).format('YYYY/MM/DD HH:mm') : '';
                                    const endDate = dates[1] ? `${moment(dates[1]).format('YYYY/MM/DD HH:mm')}` : null;
                                    setDateTimeData({ startDate: dates[0], endDate: dates[1] });
                                    // 计算日期跨度
                                    const dayDiff = moment(endDate).diff(moment(startDate), 'day');
                                    if (searchItem.dayDiffLimit && (dayDiff > searchItem.dayDiffLimit)) {
                                        // 日期跨度超过该项配置的上限
                                        GlobalNotification.error(i18n.t('business_analysis_page_the_date_interval_cannot_exceed_31'));
                                        return
                                    }
                                    if (dates[0] && dates[1]) {
                                        createCondition({
                                            value: `${startDate},${endDate}`,
                                            key: searchKey,
                                            selectValue: `${startDate} - ${endDate}`,
                                            label: selectOptionLabelToPlainString(searchItem?.label),
                                        })
                                    }
                                }
                            }
                            }
                            rangeValue={[dateTimeData.startDate, dateTimeData.endDate]}
                        />
                    </Box>
                }
                {
                    searchItem?.type === 'AUTO_COMPLETE_SELECT' &&
                    <CommonInput
                        style={{ minWidth: '200px' }}
                        inputType={'AUTO_COMPLETE_SELECT'}
                        value={selectKey}
                        onChange={(obj: any) => {
                            setSelectKey(obj);
                            if (obj) {
                                createCondition({
                                    value: obj?.value,
                                    key: searchKey,
                                    selectValue: selectOptionLabelToPlainString(obj?.label),
                                    label: selectOptionLabelToPlainString(searchItem?.label),
                                })
                            }
                        }}
                        fetchData={searchItem.fetchData}
                    />
                }
            </Box>
        </Box>
        {showConditionsBox &&
            <Box style={{ marginTop: '8px' }}>
                <ConditionsBox
                    conditions={props.conditions}
                    onDelete={(conditions) => {
                        props.onSearch(conditions);
                        setSelectKey('');
                    }}
                />
            </Box>
        }
    </Box>
})

/**
 * 已搜的搜索条件
 * @param conditions
 * @param onDelete
 * @returns
 */
export function ConditionsBox(props: { conditions: ConditionType[], onDelete: (conditions: ConditionType[]) => void }) {
    return <Box  >
        {
            props.conditions.map((item, index) => (
                <Chip
                    key={index}
                    deleteIcon={<Close />}
                    className='statusAlert mr8 mb8'
                    label={`${i18n.t(item.label)}:${item.selectValue || item.value}`}
                    onDelete={() => {
                        const conditions = props.conditions;
                        conditions.splice(index, 1);
                        props.onDelete(conditions);
                    }
                    }
                />
            ))
        }
    </Box>
}


export type SearchKeyInputItems = {
    options?: Option[];
    type: 'TEXT' | 'SELECT' | 'DATE_TIME_RANGE' | 'AUTO_COMPLETE_SELECT';
    dayDiffLimit?: number;
    fetchData?: (label?: string) => Promise<Option[]>;
    /** `type === 'TEXT'` 时搜索框 `placeholder` */
    textPlaceholder?: string;
} & SelectOption;


export type ConditionType = {
    // value
    value: string;
    // key
    key: string;
    // 左边文本
    label: string;
    // 右边文本
    selectValue?: string;
    // range 范围
    rangeValue?: string;
}